/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Fieldset, FormBlock, Button, ProgressRadial, Card, ProgressBar, ButtonGroup } from '@ruter-ds/rds-components';
import { QuestionView } from './QuestionView';
import apiRequest from '../util/apiRequest';
import './style.scss';
import { CustomerFeedbackTypes } from '../util/customerFeedbackTypes';
import { RuterStoppsImage } from './RuterStoppsImage';
import ShortUniqueId from 'short-unique-id';

import config from '../config';

export const Questionnaire = ({ stopAreaId, surveyTypeId }) => {
    const navigate = useNavigate();
    const [questions, setQuestions] = useState([]);
    const [selectedQuay, setSelectedQuay] = useState();
    const [dataSetTypeId, setDataSetTypeId] = useState();
    const [loading, setLoading] = useState(true);
    const [surveyAnswers, setSurveyAnswers] = useState({});
    const [displayFieldError, setDisplayFieldError] = useState();
    const [title, setTitle] = useState();
    const [surveyType, setSurveyType] = useState();
    const [currentQuestion, setCurrentQuestion] = useState();
    const [dataSetEntryId, setDataSetEntryId] = useState();

    useEffect(() => {
        if (stopAreaId) {
            fetchStopArea(stopAreaId);
        }
    }, [stopAreaId]);

    useEffect(() => {
        if (surveyTypeId) {
            fetchSurveyType(surveyTypeId);
        }
    }, [surveyTypeId]);

    const navigateToErrorPage = () => {
        return navigate('/error');
    };

    const fetchStopArea = (id) => {
        setLoading(true);
        apiRequest(`stopArea/${id}/flat`)
            .then((res) => {
                setSelectedQuay(res);
                setLoading(false);
                setCurrentQuestion(-1);
            })
            .catch(() => {
                navigateToErrorPage();
            });
    };

    const fetchSurveyType = (typeId) => {
        setLoading(true);
        apiRequest(`dataset/survey/type/${typeId}`)
            .then((res) => {
                if (!CustomerFeedbackTypes.includes(res.dataSetType.type) || !res.active) {
                    throw new Error();
                }
                setTitle(res.quayType);
                setDataSetTypeId(res.dataSetTypeId);
                setSurveyType(res);
                const sortQuestionByOrder = res.questions.sort((a, b) => a.order - b.order);
                setQuestions(sortQuestionByOrder);
                setCurrentQuestion(-1);
                setLoading(false);
            })
            .catch(() => {
                navigateToErrorPage();
            });
    };

    // const handleDelete = (index) => {
    //     const tempArray = [...formValue];
    //     tempArray.splice(index, 1);
    //     setFormValue(tempArray);
    // };

    const onChange = (attrId, value) => {
        setSurveyAnswers({ ...surveyAnswers, [attrId]: value });
    };

    const startSurvey = () => {
        setCurrentQuestion(0);
    };

    const validateCurrentAnswer = () => {
        const question = questions[currentQuestion];
        if (question.required && !surveyAnswers[question.id]) {
            setDisplayFieldError('Spørsmålet er påkrevd');
            return false;
        }
        if (question.format === 'number' && surveyAnswers[question.id] && isNaN(surveyAnswers[question.id])) {
            setDisplayFieldError('Du må oppi et gyldig tall');
            return false;
        }
        setDisplayFieldError('');
        return true;
    };

    const submitAnswer = () => {
        if (!validateCurrentAnswer()) {
            return;
        }

        const currentAttributeId = questions[currentQuestion].id;
        const currentAnswer = surveyAnswers[currentAttributeId];

        const valuesToSubmit = [
            {
                attributeId: parseInt(currentAttributeId),
                value: Array.isArray(currentAnswer) ? JSON.stringify(currentAnswer) : currentAnswer,
            },
        ];

        let body;
        let method;
        let url;

        if (!dataSetEntryId) {
            body = {
                stopAreaId: stopAreaId,
                dataSetTypeId: dataSetTypeId,
                surveyTypeId: surveyTypeId,
                data: valuesToSubmit,
                lastAnsweredQuestionId: questions[currentQuestion].id,
            };
            method = 'POST';
            url = `dataset/customerFeedback`;
        } else {
            body = {
                id: dataSetEntryId,
                dataPoints: valuesToSubmit,
                lastAnsweredQuestionId: questions[currentQuestion].id,
            };
            method = 'PUT';
            url = `dataset/customerFeedback/entry/${dataSetEntryId}`;
        }
        if (currentQuestion + 1 === questions.length && surveyType.requiresCompletionCode) {
            const uid = new ShortUniqueId({ length: 10 });
            const code = uid();
            body['verificationCode'] = code;
        }

        apiRequest(url, method, body)
            .then((res) => {
                if (currentQuestion + 1 === questions.length) {
                    sessionStorage.setItem('surveyDone', true);
                    sessionStorage.setItem('selectedStopId', stopAreaId);
                    sessionStorage.setItem('provideCode', surveyType.requiresCompletionCode ? 'yes' : 'no');
                    sessionStorage.setItem('code', res.verificationCode);
                    navigate('/receipt');
                } else {
                    setCurrentQuestion(currentQuestion + 1);
                    if (!dataSetEntryId) {
                        setDataSetEntryId(res.id);
                    }
                }
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const stepBack = () => {
        setCurrentQuestion(currentQuestion - 1);
    };

    if (!surveyAnswers || !surveyType) {
        return null;
    }

    const descriptionLines = surveyType.description ? surveyType.description.split('\n') : [''];

    return (
        <Container className="rds-my--s customerFeedback">
            {loading && <ProgressRadial isIndeterminate />}
            {!loading && (
                <div>
                    <div className="header rds-p--s">
                        <h2 className="rs-echo">{selectedQuay?.publicName}</h2>
                        <p>
                            <span>Id: {selectedQuay?.hastusId}</span>
                        </p>
                    </div>
                    <div className="rds-mt--l">
                        <form>
                            <FormBlock>
                                <Fieldset legend={title}>
                                    <div className="questions">
                                        {currentQuestion >= 0 ? (
                                            <Fragment>
                                                <div>{`${currentQuestion + 1}/${questions.length}`}</div>
                                                <ProgressBar value={((currentQuestion + 1) / questions.length) * 100} />
                                                <QuestionView
                                                    question={questions[currentQuestion]}
                                                    onChange={onChange}
                                                    currentValue={surveyAnswers[questions[currentQuestion].id]}
                                                    displayError={displayFieldError}
                                                    setDisplayError={setDisplayFieldError}
                                                />
                                            </Fragment>
                                        ) : (
                                            <Card className="rds-my--m rds-mr--xs rs-charlie rs-flex-grow questionCard">
                                                <div className="rds-m--m">
                                                    {surveyType.picture && (
                                                        <RuterStoppsImage
                                                            objectFit="contain"
                                                            style={{ maxWidth: 200, maxHeight: 200 }}
                                                            className="questionTypeImage"
                                                            alt={surveyType.description}
                                                            src={`${config.serverUrl}document/download/lowRes/${surveyType.picture?.filepath}`}
                                                        />
                                                    )}
                                                    <div className="rds-my--s rds-mb--l">
                                                        {descriptionLines.map((line, i) => (
                                                            <p key={`desc-line-${i}`}>{line}</p>
                                                        ))}
                                                    </div>
                                                </div>
                                            </Card>
                                        )}
                                    </div>
                                </Fieldset>
                            </FormBlock>

                            <div className="rds-my--s">
                                {currentQuestion >= 0 ? (
                                    <ButtonGroup loose>
                                        <Button
                                            type="button"
                                            shape="pill"
                                            skin="info"
                                            variant="outline"
                                            text="Tilbake"
                                            onClick={stepBack}
                                        />
                                        <Button
                                            type="button"
                                            shape="pill"
                                            skin="info"
                                            text={currentQuestion + 1 === questions.length ? 'Send' : 'Neste'}
                                            onClick={submitAnswer}
                                        />
                                    </ButtonGroup>
                                ) : (
                                    <Button type="button" shape="pill" skin="info" text="Start undersøkelsen" onClick={startSurvey} />
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Container>
    );
};
